import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { fieldValueToString } from "../utils/react_hook_form_input_utils";
import { Controller, UseFormReturn } from "react-hook-form";

/**
 * DefaultFormSelectInputOption
 */

export type DefaultFormSelectInputOption = { id: number, label: string } | { id: string, label: string };

/**
 * DefaultFormSelectInput
 */

type Props<T extends {}> = {
  name: keyof T,
  label: string,
  required?: boolean,
  options: DefaultFormSelectInputOption[],
  initialOptionId?: number | string,
  initialOptionLabel?: string,
  showSelectionLabel?: boolean,
  disabled?: boolean;
  readOnly?: boolean;
  reactHookForm: UseFormReturn<T>,
  onChangeValue?: (value: number) => void;
};

export const DefaultFormSelectInput = <T extends {},>({
  name,
  label,
  required,
  options,
  initialOptionId = '',
  initialOptionLabel = 'Selecionar',
  showSelectionLabel = true,
  disabled,
  readOnly,
  reactHookForm,
  onChangeValue,
}: Props<T>) => {
  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let helperText: string | null = null;

        if (formState.errors[name as keyof typeof formState.errors]) {
          const fieldError = formState.errors[name] as any;
          if (fieldError.message && typeof fieldError.message === 'string') {
            helperText = fieldError.message;
          }
        }

        return (
          <FormControl
            size="small"
            fullWidth={true}
            required={required}>
            <InputLabel
              id={`select-${name as string}`}
              size="small"
              error={helperText !== null && helperText !== undefined}
              required={required}>
              {label}
            </InputLabel>

            <Select
              {...field}
              size="small"
              labelId={`select-${name as string}`}
              label={label}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              value={fieldValueToString(field.value) ?? ''}
              onChange={e => {
                if (onChangeValue) {
                  if (typeof e.target.value === 'string') {
                    onChangeValue(Number(e.target.value));
                  } else if (typeof e.target.value === 'number') {
                    onChangeValue(e.target.value);
                  } else {
                    throw new Error('Unexpected type!');
                  }
                }
                field.onChange(e);
              }}>
              {showSelectionLabel && <MenuItem value={initialOptionId}>{initialOptionLabel}</MenuItem>}
              {options.map(x => <MenuItem key={x.id} value={x.id}>{x.label}</MenuItem>)}
            </Select>

            <FormHelperText
              id={`select-${name as string}-helper`}
              error={helperText !== null && helperText !== undefined}>
              {helperText}
            </FormHelperText>
          </FormControl>
        )
      }} />
  );
}